import type { InferType } from 'groqd'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'

import Spinner from '@nxt-insurance/ui-kit/components/Spinner'
import { ICON_SIZE } from '@nxt-insurance/ui-kit/constants'
import { body2, h4 } from '@nxt-insurance/ui-kit/core/typography'

import { runQuery } from '@/cms/client'
import { categoriesQuery, postsQuery } from '@/components/renderers/BlogOverview/queries'
import { blogItemDescription, navigationMenu } from '@/components/renderers/BlogOverview/styles'
import { DOMAIN } from '@/config/env'
import { getLocaleParts } from '@/utils/locale'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { useQuery } from '@tanstack/react-query'

const HeroImage = dynamic(async () => import('./HeroImage').then((mod) => mod.HeroImage), {
  loading: () => <Spinner />, // TODO: change spinner to "skeleton" https://hellogetsafe.atlassian.net/browse/HAPPY-581
  ssr: false,
})

const BlogItem = (props: { post: InferType<typeof postsQuery>[number] }) => {
  const { t } = useTranslation()
  return (
    <Link
      id={`blog-overview-item-${props.post.slug}`}
      className={twMerge(
        'blog-overview-item',
        'flex flex-col overflow-hidden rounded-lg border border-solid border-ui-grey-500 bg-ui-light no-underline transition-all delay-300 ease-linear hover:-translate-y-1.5 hover:shadow-ui-grey'
      )}
      href={`/posts/${props.post.slug}`}
      passHref
    >
      <div className="relative flex h-[225px] w-full flex-col">
        <HeroImage heroImage={props.post.heroImage} />
      </div>
      <div className="flex min-h-[230px] flex-1 flex-col justify-between gap-y-4 p-8 text-ui-dark md:p-5">
        <p className={h4()}>{props.post.heading}</p>
        <p className={blogItemDescription()}>{props.post.description}</p>
        <div className="flex flex-col items-center gap-y-2.5">
          <Image
            src="/assets/images/logo_green.svg"
            width={ICON_SIZE.medium}
            height={ICON_SIZE.medium}
            loader={({ src }) => src}
            unoptimized
            alt={props.post.author.firstName}
          />
          <div className=" flex-1">
            {props.post.author.firstName} ·{' '}
            {t('blog.articleDetails.minuteReadText', {
              readingTime: props.post.readingTime.toString(),
            })}
          </div>
        </div>
      </div>
    </Link>
  )
}

export const BlogOverview = () => {
  const { locale, query, asPath } = useRouter()
  const [lang, market] = getLocaleParts(locale)
  const categoryFromQuery = Array.isArray(query.category) ? query.category[0] : query.category
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['blogCategories', locale],
    queryFn: async () =>
      runQuery(categoriesQuery, {
        lang,
      }),
    throwOnError: true,
  })
  const { data: posts, isLoading: isLoadingPosts } = useQuery({
    queryKey: ['blogPosts', locale, categoryFromQuery],
    queryFn: async () =>
      runQuery(postsQuery, {
        lang,
        market,
        category: categoryFromQuery || null,
      }),
    throwOnError: true,
  })
  const getHref = (category: string) => {
    const url = new URL(asPath, `https://${DOMAIN}`)
    url.searchParams.delete('category')
    url.searchParams.append('category', category)
    return `${url.pathname}?${url.searchParams.toString()}`
  }
  if (isLoadingCategories) {
    return (
      <div className="flex w-full flex-col items-center justify-center">
        <div className="w-full max-w-screen-2xl p-8">
          <Spinner />
        </div>
      </div>
    )
  }
  if (!categories) {
    return null
  }

  const { root, list, link } = navigationMenu()

  const currentCategory = categoryFromQuery ?? categories[0].slug
  return (
    <div className="flex w-full flex-col items-center justify-center">
      <div className="w-full max-w-screen-2xl p-8">
        <NavigationMenu.Root className={root()}>
          <NavigationMenu.List className={list()}>
            {categories.map((category) => (
              <NavigationMenu.Item key={category._id} value={category.slug}>
                <Link id={`blog-category-${category.slug}`} passHref href={getHref(category.slug)}>
                  <NavigationMenu.Link
                    className={link()}
                    active={category.slug === currentCategory}
                  >
                    <span className={body2()}>{category.title}</span>
                  </NavigationMenu.Link>
                </Link>
              </NavigationMenu.Item>
            ))}
          </NavigationMenu.List>
        </NavigationMenu.Root>
        {isLoadingPosts ? (
          <div className="flex w-full flex-col items-center justify-center p-4">
            <Spinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 px-0 py-5 md:grid-cols-2">
            {posts ? posts.map((post) => <BlogItem key={post._id} post={post} />) : null}
          </div>
        )}
      </div>
    </div>
  )
}
